import { Controller } from "@hotwired/stimulus";

/**
 * Channel Selector Controller
 *
 * Manages the neighbor compose form channel selector:
 * - Toggles SMS character counter visibility when channel changes (FR-5)
 * - Updates character count and segment count on textarea input (FR-5)
 * - Handles Cmd+Enter / Ctrl+Enter keyboard shortcut for submit (FR-4)
 * - Validates form (message required) and manages send button state
 */
export default class extends Controller {
  static targets = ["form", "messageContent", "charCounter", "sendButton"];

  static values = {
    selectedChannel: { type: String, default: "" },
  };

  declare readonly formTarget: HTMLFormElement;
  declare readonly hasFormTarget: boolean;
  declare readonly messageContentTarget: HTMLTextAreaElement;
  declare readonly hasMessageContentTarget: boolean;
  declare readonly charCounterTarget: HTMLElement;
  declare readonly hasCharCounterTarget: boolean;
  declare readonly sendButtonTarget: HTMLButtonElement;
  declare readonly hasSendButtonTarget: boolean;

  declare selectedChannelValue: string;

  connect() {
    this.syncSelectedChannel();
    this.toggleCharCounter();
    this.updateCharCounter();
    this.updateSendButtonState();
  }

  channelChanged(event: Event) {
    const target = event.target as HTMLInputElement;
    this.selectedChannelValue = target.value;
    this.toggleCharCounter();
    this.updateSendButtonState();
  }

  messageChanged() {
    this.updateCharCounter();
  }

  handleSubmit(event: SubmitEvent) {
    if (!this.isFormValid()) {
      event.preventDefault();
      return;
    }

    this.setLoading(true);
  }

  submitForm(event: KeyboardEvent) {
    if (!this.isFormValid()) return;

    if (this.hasFormTarget) {
      event.preventDefault();
      this.formTarget.requestSubmit();
    }
  }

  private toggleCharCounter() {
    if (!this.hasCharCounterTarget) return;

    if (this.selectedChannelValue === "sms") {
      this.charCounterTarget.classList.remove("hidden");
      this.updateCharCounter();
    } else {
      this.charCounterTarget.classList.add("hidden");
    }
  }

  private updateCharCounter() {
    if (!this.hasCharCounterTarget || !this.hasMessageContentTarget) return;
    if (this.selectedChannelValue !== "sms") return;

    const text = this.messageContentTarget.value;
    const length = text.length;
    const { maxChars } = this.calculateSegments(length);

    if (length === 0) {
      this.charCounterTarget.textContent = "";
      return;
    }

    this.charCounterTarget.textContent = `${length}/${maxChars}`;

    // Warn at 80%+ of current segment capacity
    const usageRatio = length / maxChars;
    if (usageRatio >= 0.8) {
      this.charCounterTarget.classList.add("text-warning");
    } else {
      this.charCounterTarget.classList.remove("text-warning");
    }
  }

  /**
   * Calculate SMS segments based on GSM-7 encoding rules:
   * - Single message: 160 chars
   * - Multi-part: 153 chars per segment (7 chars reserved for UDH header)
   */
  calculateSegments(length: number): { segments: number; maxChars: number } {
    if (length === 0) return { segments: 1, maxChars: 160 };
    if (length <= 160) return { segments: 1, maxChars: 160 };

    const segments = Math.ceil(length / 153);
    return { segments, maxChars: segments * 153 };
  }

  private isFormValid(): boolean {
    const hasMessage =
      this.hasMessageContentTarget &&
      this.messageContentTarget.value.trim().length > 0;
    const hasChannel = this.selectedChannelValue !== "";
    return hasMessage && hasChannel;
  }

  private setLoading(loading: boolean) {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled =
        loading || !this.hasEnabledChannelOption();

      if (loading) {
        this.sendButtonTarget.classList.add("loading");
      } else {
        this.sendButtonTarget.classList.remove("loading");
      }
    }
  }

  private syncSelectedChannel() {
    const checkedOption = this.channelOptions.find((option) => option.checked);
    this.selectedChannelValue = checkedOption?.value || "";
  }

  private updateSendButtonState() {
    if (!this.hasSendButtonTarget) return;

    this.sendButtonTarget.disabled = !this.hasEnabledChannelOption();
  }

  private hasEnabledChannelOption(): boolean {
    return this.channelOptions.some((option) => !option.disabled);
  }

  private get channelOptions(): HTMLInputElement[] {
    return Array.from(
      this.element.querySelectorAll('input[name="channel"]')
    ) as HTMLInputElement[];
  }
}
